// src/pages/Outlook2027.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbLd, faqLd, itemListLd } from '../components/common/SEO';
import Hero from '../components/common/Hero';
import Breadcrumb from '../components/common/Breadcrumb';
import Sidebar from '../components/common/Sidebar';
import InContentAd from '../components/ads/InContentAd';
import Icon from '../components/common/Icon';
import { outlookItems, budget2027 } from '../data/outlook2027';
import { categories, getCategory } from '../data/categories';

const faq = [
    {
        q: '2027년 정부 예산 규모는 얼마인가요?',
        a: '2027년 정부 예산안 총지출은 820조 9,000억원으로, 2026년 대비 93조원(12.8%) 늘어난 역대 최대 규모입니다. 162조 3,000억원 규모의 미래대응기금도 새로 만들어집니다.',
    },
    {
        q: '2027년 출산지원금은 얼마를 받을 수 있나요?',
        a: '정부 예산안 기준으로 첫째 1,000만원, 셋째 이상 1,500만원이며 지방 거주 가구는 최대 2,000만원까지 지원받을 수 있도록 설계되었습니다. 여기에 혼인지원금 100만원과 월 20만~30만원의 아동기본수당이 더해져 자녀 1명당 최대 7,500만원 규모가 됩니다.',
    },
    {
        q: '2027년 청년 지원은 어떻게 달라지나요?',
        a: '청년 예산이 28조 2,000억원에서 43조 3,000억원으로 53.5% 늘어납니다. 청년첫취업지원제도(월 65만원, 16만명)가 신설되고, 청년미래적금의 소득 요건이 폐지되며, 청년월세 지원 소득 기준이 기준 중위소득 100% 이하로 완화됩니다.',
    },
    {
        q: '2027년 예산안은 확정된 내용인가요?',
        a: '아닙니다. 2026년 9월 1일 발표된 정부 예산안으로, 국회 심의·의결을 거쳐 연말에 확정됩니다. 심의 과정에서 사업별 금액과 시행 기준이 조정될 수 있습니다.',
    },
];

const Outlook2027: React.FC = () => {
    const crumbs = [
        { name: '홈', path: '/' },
        { name: '2027년 정부지원금 전망', path: '/2027' },
    ];

    const budgetCards = [
        { label: '2027년 총지출', value: '820.9조원', sub: budget2027.increase },
        { label: '청년 지원 예산', value: '43.3조원', sub: '전년 대비 +53.5%' },
        { label: '미래대응기금', value: '162.3조원', sub: '신설' },
        { label: '기준 중위소득', value: '+6.7%', sub: '역대 최대 인상률' },
    ];

    return (
        <>
            <SEO
                path="/2027"
                title="2027년 정부지원금 전망 - 예산안 821조, 출산지원금·청년예산 총정리"
                description="2026년 9월 1일 발표된 2027년 정부 예산안 기준 전망. 총지출 820조 9천억원, 혼인지원금 100만원·출산지원금 최대 2,000만원 신설, 청년예산 43.3조원, 기준 중위소득 6.7% 인상 등 달라질 지원금을 정리했습니다."
                keywords="2027 정부지원금, 2027 예산안, 2027 출산지원금, 혼인지원금, 아동기본수당, 청년미래적금, 우리아이자립펀드, 2027 기준 중위소득, 청년첫취업지원제도"
                updated="2026-09-02"
                jsonLd={[
                    breadcrumbLd(crumbs),
                    faqLd(faq),
                    itemListLd(
                        '2027년 신설·확대 정부지원금',
                        outlookItems.map(o => ({ title: o.title, path: '/2027' }))
                    ),
                ]}
            />

            <div className="gh-layout">
                <div>
                    <Breadcrumb items={crumbs} />

                    <Hero
                        eyebrow="2026.09.01 정부 예산안 발표 기준"
                        eyebrowIcon="chart"
                        title="2027년 정부지원금, 무엇이 달라질까"
                        description={
                            <>
                                총지출 <strong>820조 9천억원</strong>의 역대 최대 예산안이 발표됐습니다.
                                혼인·출산지원금 신설과 청년 예산 <strong>53.5% 확대</strong>가 핵심입니다.
                            </>
                        }
                        stats={[
                            { value: '820.9조원', label: '2027년 총지출' },
                            { value: '43.3조원', label: '청년 예산' },
                            { value: '+6.7%', label: '기준 중위소득' },
                        ]}
                        art="chart"
                    />

                    <div className="gh-callout gh-callout--amber">
                        <Icon name="shield" size={20} />
                        <div>
                            <div className="gh-callout__title">읽기 전에 확인하세요</div>
                            <p className="gh-callout__text">{budget2027.note}</p>
                        </div>
                    </div>

                    {/* 예산 개요 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="coin" size={19} />
                                    2027년 예산안 한눈에
                                </h2>
                                <p className="gh-sec__desc">기획재정부 2027년 예산안 (2026.09.01 발표)</p>
                            </div>
                        </div>
                        <div className="gh-budget">
                            {budgetCards.map(b => (
                                <div className="gh-budget__item" key={b.label}>
                                    <div className="gh-budget__label">{b.label}</div>
                                    <div className="gh-budget__value">{b.value}</div>
                                    <div className="gh-budget__sub">{b.sub}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* 신설·확대 제도 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="sparkle" size={19} />
                                    2027년 <em>신설·확대</em>되는 지원 제도
                                </h2>
                                <p className="gh-sec__desc">총 {outlookItems.length}개 주요 사업</p>
                            </div>
                        </div>

                        <div className="gh-outlook">
                            {outlookItems.map(o => (
                                <article className="gh-outlook__item" key={o.id}>
                                    <div className="gh-outlook__figure">
                                        <div className="gh-outlook__fig">{o.figure}</div>
                                        <div className="gh-outlook__figlabel">{o.figureLabel}</div>
                                    </div>
                                    <div className="gh-outlook__main">
                                        <h3 className="gh-outlook__title">
                                            {o.title}
                                            <span className={`gh-status gh-status--${o.status}`}>{o.status}</span>
                                            <span className="gh-card__cat">{getCategory(o.category).shortName}</span>
                                        </h3>
                                        <p className="gh-outlook__detail">{o.detail}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </section>

                    <InContentAd />

                    {/* 분야별 요약 */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name="grid" size={19} />
                                    분야별 2027년 전망
                                </h2>
                            </div>
                        </div>
                        <div className="gh-cards">
                            {categories.slice(1).map(c => {
                                const items = outlookItems.filter(o => o.category === c.id);
                                if (items.length === 0) return null;
                                return (
                                    <Link to={c.path} className="gh-card" key={c.id}>
                                        <div className="gh-card__top">
                                            <span className="gh-card__cat">{c.shortName}</span>
                                            <span className="gh-card__flag gh-card__flag--new">
                                                {items.length}건
                                            </span>
                                        </div>
                                        <h3 className="gh-card__title">{c.name}</h3>
                                        <p className="gh-card__summary">
                                            {items.map(i => i.title).join(' · ')}
                                        </p>
                                        <div className="gh-card__foot">
                                            <span className="gh-card__agency">2027년 예산안 기준</span>
                                            <span className="gh-card__link">
                                                현재 제도 보기
                                                <Icon name="chevronRight" size={14} strokeWidth={2.2} />
                                            </span>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>

                    {/* FAQ */}
                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <h2 className="gh-sec__title">
                                <Icon name="document" size={19} />
                                자주 묻는 질문
                            </h2>
                        </div>
                        <div className="gh-faq">
                            {faq.map((f, i) => (
                                <div className="gh-faq__item is-open" key={i}>
                                    <div className="gh-faq__q" style={{ cursor: 'default' }}>
                                        <span className="gh-faq__mark">Q</span>
                                        {f.q}
                                    </div>
                                    <p className="gh-faq__a">{f.a}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <div className="gh-callout">
                        <Icon name="sparkle" size={20} />
                        <div>
                            <div className="gh-callout__title">지금 신청할 수 있는 지원금이 궁금하다면</div>
                            <p className="gh-callout__text">
                                2027년 제도는 아직 예산안 단계입니다. 지금 바로 신청 가능한 2026년 지원금을
                                먼저 확인해 보세요.{' '}
                                <Link to="/2026" style={{ color: 'var(--blue-700)', fontWeight: 700 }}>
                                    2026년 달라진 점 보기 →
                                </Link>
                            </p>
                        </div>
                    </div>

                </div>

                <Sidebar />
            </div>
        </>
    );
};

export default Outlook2027;
